import React from 'react';
import PropTypes from 'prop-types';

import { JumperNumberCell } from './JumperNumberCell';
import { PlayerCell } from './PlayerCell';
import { EmptyCell } from './EmptyCell';

/**
 * Jumper number custom cell, rendered via a `customCells` entry passed to
 * `StatsTableBody`.
 *
 * @param {object} props - Props.
 * @param {object} props.cell - React Table cell instance.
 * @param {object} props.cellProps - Cell prop getters from React Table.
 * @param {boolean} props.isMainCompetition - Whether the competition supports player profile links.
 * @param {boolean} props.isAFLW - Whether the competition is AFLW.
 * @returns {JSX.Element} Component.
 */
const JumperNumberCellRenderer = ({
    cell,
    cellProps,
    isMainCompetition,
    isAFLW
}) => (
    <JumperNumberCell
        cell={cell}
        cellProps={cellProps}
        isMainCompetition={isMainCompetition}
        isAFLW={isAFLW}
    />
);

JumperNumberCellRenderer.propTypes = {
    cell: PropTypes.object.isRequired,
    cellProps: PropTypes.object.isRequired,
    isMainCompetition: PropTypes.bool,
    isAFLW: PropTypes.bool
};

/**
 * Creates a jumper number `customCells` renderer bound to the given
 * competition context.
 *
 * @param {boolean} isMainCompetition - Whether the competition supports player profile links.
 * @param {boolean} isAFLW - Whether the competition is AFLW.
 * @returns {Function} Cell renderer accepting (row, cell, cellProps).
 */
export const createJumperNumberCellRenderer =
    (isMainCompetition, isAFLW) => (row, cell, cellProps) =>
        (
            <JumperNumberCellRenderer
                key={cellProps.key}
                cell={cell}
                cellProps={cellProps}
                isMainCompetition={isMainCompetition}
                isAFLW={isAFLW}
            />
        );

/**
 * Player custom cell, rendered via a `customCells` entry passed to
 * `StatsTableBody`.
 *
 * @param {object} props - Props.
 * @param {object} props.cell - React Table cell instance.
 * @param {object} props.cellProps - Cell prop getters from React Table.
 * @param {boolean} props.isMainCompetition - Whether the competition supports player profile links.
 * @param {boolean} props.isAFLW - Whether the competition is AFLW.
 * @param {object} props.benchReasonMap - Map of playerId to bench reason.
 * @returns {JSX.Element} Component.
 */
const PlayerCellRenderer = ({
    cell,
    cellProps,
    isMainCompetition,
    isAFLW,
    benchReasonMap
}) => (
    <PlayerCell
        cell={cell}
        cellProps={cellProps}
        isMainCompetition={isMainCompetition}
        isAFLW={isAFLW}
        benchReasonMap={benchReasonMap}
    />
);

PlayerCellRenderer.propTypes = {
    cell: PropTypes.object.isRequired,
    cellProps: PropTypes.object.isRequired,
    isMainCompetition: PropTypes.bool,
    isAFLW: PropTypes.bool,
    benchReasonMap: PropTypes.object.isRequired
};

/**
 * Creates a player `customCells` renderer bound to the given competition
 * context.
 *
 * @param {boolean} isMainCompetition - Whether the competition supports player profile links.
 * @param {boolean} isAFLW - Whether the competition is AFLW.
 * @param {object} benchReasonMap - Map of playerId to bench reason.
 * @returns {Function} Cell renderer accepting (row, cell, cellProps).
 */
export const createPlayerCellRenderer =
    (isMainCompetition, isAFLW, benchReasonMap) => (row, cell, cellProps) =>
        (
            <PlayerCellRenderer
                key={cellProps.key}
                cell={cell}
                cellProps={cellProps}
                isMainCompetition={isMainCompetition}
                isAFLW={isAFLW}
                benchReasonMap={benchReasonMap}
            />
        );

/**
 * Empty custom cell, rendered via a `customCells` entry passed to
 * `StatsTableBody`.
 *
 * @param {object} props - Props.
 * @param {object} props.cellProps - Cell prop getters from React Table.
 * @returns {JSX.Element} Component.
 */
const EmptyCellRenderer = ({ cellProps }) => (
    <EmptyCell cellProps={cellProps} />
);

EmptyCellRenderer.propTypes = {
    cellProps: PropTypes.object.isRequired
};

/**
 * Empty `customCells` renderer. Needs no competition context, so it is used
 * directly rather than via a factory.
 *
 * @param {object} row - React Table row instance.
 * @param {object} cell - React Table cell instance.
 * @param {object} cellProps - Cell prop getters from React Table.
 * @returns {JSX.Element} Component.
 */
export const renderEmptyCell = (row, cell, cellProps) => (
    <EmptyCellRenderer key={cellProps.key} cellProps={cellProps} />
);
